/* Display-side text hygiene. No DOM, no Lit, no React — pure string work.

   Ported from v1 (app/static/app.js), where it was proven against the live
   library. The rendering half deliberately isn't here: this layer stays
   framework-free, so `splitSnippet` returns segments and the component decides
   what a highlight looks like.

   Moved out of web/src/core/ into packages/core in Phase 1 of the native
   client, so the Expo app and the Lit app share one implementation rather than
   two that drift. Read packages/core/README.md before adding an import here —
   this package must stay dependency-free, and its relative imports must stay
   extensionless. */

/* ── mojibake ──
   Instagram captions were ingested as UTF-8 but mis-decoded as Latin-1, so emoji,
   ₹ and curly quotes land as "wouldnâ€™t" / "â‚¹2,500". Reversed for DISPLAY only —
   the raw caption stays authoritative in the DB — by decoding each run of
   U+0080–U+00FF back through UTF-8. Genuine Unicode (the Devanagari transcripts,
   U+0900+) is out of that range and passes through untouched, as does a run that
   isn't valid UTF-8, which is what real Latin-1 text looks like. */

/* Lazy, and that matters now that this module is shared.
 *
 * `TextDecoder` is not a React Native global — RN 0.86 ships none. It is Expo's:
 * `expo/src/winter/runtime.native.ts` installs a UTF-8-only implementation at
 * app entry, and it honours `fatal` by throwing, which is exactly the branch
 * below. But "at app entry" is an ordering promise, and it held only because
 * `apps/mobile/index.ts` imports `expo` before `./App`. Constructing at module
 * scope bet the whole module on that ordering surviving a jest `setupFiles`
 * entry, a reordered entry point, or a future Expo change. One line removes the
 * bet. Reuse across calls is safe: `decode()` resets its own state whenever it
 * is not streaming, and we never pass `{ stream: true }`. */
let utf8: TextDecoder | undefined;
const decoder = () => (utf8 ??= new TextDecoder('utf-8', { fatal: true }));
/* Built from char codes rather than written as literals, as in v1: the range
   ends are invisible control characters, and an editor or a tool that
   normalised one would break the match with nothing to see in the diff. */
const ch = String.fromCharCode;
/** A UTF-8 lead byte. Absent -> the string has no mojibake, so skip the work. */
const MOJI_LEAD = new RegExp('[' + ch(0xc2) + '-' + ch(0xf4) + ']');
const MOJI_RUN = new RegExp('[' + ch(0x80) + '-' + ch(0xff) + ']+', 'g');

export function fixMojibake(s: string): string {
  if (!s || !MOJI_LEAD.test(s)) return s;
  return s.replace(MOJI_RUN, (run) => {
    try {
      return decoder().decode(Uint8Array.from(run, (c) => c.charCodeAt(0)));
    } catch {
      return run; // genuine Latin-1, not a mis-decode
    }
  });
}

/**
 * `fixMojibake` plus blanking the literal strings that leak out of the analysis
 * step ("None", "null", "N/A") — for short display fields where rendering the
 * word is worse than rendering nothing.
 */
export function clean(s: string | null | undefined): string {
  const t = fixMojibake((s ?? '').trim());
  return /^(none|null|undefined|n\/?a)$/i.test(t) ? '' : t;
}

/** `137` → `2:17`. Whole minutes and zero-padded seconds, as v1.
 *
 * Rounds to whole seconds FIRST, then splits. Splitting first and rounding the
 * remainder produced `0:60` for anything in [59.5, 60): the minute was floored
 * before the second was rounded up past it. Durations arrive from the DB as
 * ints so it may never have fired in practice, but the shared version is the
 * one a second client will now depend on. */
export function fmtDuration(seconds: number): string {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** One run of snippet text, flagged if FTS matched inside it. */
export interface SnippetPart {
  text: string;
  mark: boolean;
}

/* SQLite's snippet() wraps matches in these, set in query.py. They are outside
   the mojibake byte range, so fixing the text first leaves them intact. */
const OPEN = '⟪';
const CLOSE = '⟫';

/**
 * Split an FTS snippet into plain and highlighted runs.
 *
 * v1 built `<mark>` tags by string replacement and handed the result to
 * innerHTML — safe only because it escaped first, one ordering mistake away from
 * injecting transcript text as markup. Returning segments removes the hazard
 * entirely: the caller renders each part as a Lit child, which cannot be markup.
 */
export function splitSnippet(raw: string): SnippetPart[] {
  const text = fixMojibake(raw || '');
  if (!text) return [];
  const parts: SnippetPart[] = [];
  // Split keeps the delimiters out, and the open/close markers strictly
  // alternate, so a run's index parity is what tells us whether it matched.
  for (const [i, chunk] of text.split(new RegExp(`[${OPEN}${CLOSE}]`)).entries()) {
    if (chunk) parts.push({ text: chunk, mark: i % 2 === 1 });
  }
  return parts;
}
